import { useCallback, useEffect, useRef, useState } from "react";
import SignaturePadLib from "signature_pad";
import { useTranslation } from "react-i18next";

type SignaturePadProps = {
  /** Receives a trimmed PNG data URI, or null when the pad is empty. */
  onChange: (dataUrl: string | null) => void;
  disabled?: boolean;
  /**
   * Canvas height in CSS pixels. The pad is only ever mounted inside the
   * full-screen signing overlay, so it is sized like the RN
   * `signaturePadFullScreenContainer` (70% of the screen, clamped to 200-300)
   * rather than like a footer slot.
   */
  canvasHeight?: number;
};

/** Default canvas height, sized like the RN full-screen signing pad. */
export const SIGNATURE_CANVAS_HEIGHT_PX = 260;

/**
 * A click that leaves a single dot must not pass for a signature, so a pad only
 * counts as signed once the ink covers at least this many CSS pixels in each
 * dimension. A real signature is far larger than one stray tap.
 */
const MIN_INK_WIDTH_PX = 24;
const MIN_INK_HEIGHT_PX = 8;
/** Transparent padding kept around the cropped signature when it is exported. */
const TRIM_PADDING_PX = 6;

/**
 * Crops a signature to the bounding box of its ink.
 *
 * `signature_pad` exports the whole canvas, so a signature that occupies a
 * corner of a wide pad would otherwise be inlined into the signed agreement as a
 * mostly-empty PNG. The agreement is stored as one base64 `data:text/html`
 * document, and that document also carries the society chop, so an untrimmed
 * canvas inflates every stored copy of the agreement.
 */
async function trimToInk(dataUrl: string): Promise<string> {
  const image = new Image();
  image.src = dataUrl;
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error("signature image failed to load"));
  });

  const source = document.createElement("canvas");
  source.width = image.width;
  source.height = image.height;
  const sourceContext = source.getContext("2d");
  if (!sourceContext) return dataUrl;
  sourceContext.drawImage(image, 0, 0);

  let minX = source.width;
  let minY = source.height;
  let maxX = -1;
  let maxY = -1;
  const pixels = sourceContext.getImageData(0, 0, source.width, source.height).data;
  for (let y = 0; y < source.height; y += 1) {
    for (let x = 0; x < source.width; x += 1) {
      // Anything with alpha counts as ink; the pad draws in a single colour.
      if (pixels[(y * source.width + x) * 4 + 3] > 16) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (maxX < 0 || maxY < 0) return dataUrl;

  const left = Math.max(0, minX - TRIM_PADDING_PX);
  const top = Math.max(0, minY - TRIM_PADDING_PX);
  const right = Math.min(source.width, maxX + TRIM_PADDING_PX + 1);
  const bottom = Math.min(source.height, maxY + TRIM_PADDING_PX + 1);
  if (right - left < MIN_INK_WIDTH_PX || bottom - top < MIN_INK_HEIGHT_PX) {
    return "";
  }

  const cropped = document.createElement("canvas");
  cropped.width = right - left;
  cropped.height = bottom - top;
  const croppedContext = cropped.getContext("2d");
  if (!croppedContext) return dataUrl;
  croppedContext.drawImage(source, left, top, right - left, bottom - top, 0, 0, right - left, bottom - top);
  return cropped.toDataURL("image/png");
}

export default function SignaturePad({
  onChange,
  disabled,
  canvasHeight = SIGNATURE_CANVAS_HEIGHT_PX,
}: SignaturePadProps) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const padRef = useRef<SignaturePadLib | null>(null);
  const [hasInk, setHasInk] = useState(false);

  const publish = useCallback(async () => {
    const pad = padRef.current;
    if (!pad) return;
    if (pad.isEmpty()) {
      setHasInk(false);
      onChange(null);
      return;
    }
    try {
      const trimmed = await trimToInk(pad.toDataURL("image/png"));
      // `trimToInk` returns "" for ink too small to be a signature.
      setHasInk(Boolean(trimmed));
      onChange(trimmed || null);
    } catch {
      setHasInk(false);
      onChange(null);
    }
  }, [onChange]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ratio = Math.max(1, window.devicePixelRatio || 1);
    const rect = canvas.getBoundingClientRect();
    const width = rect.width || 480;
    const height = rect.height || canvasHeight;
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const pad = new SignaturePadLib(canvas, {
      minWidth: 1,
      maxWidth: 2.5,
      penColor: "#111",
      backgroundColor: "rgba(255,255,255,0)",
    });
    // The pad draws in canvas pixels, so the context has to be scaled for the
    // strokes to land on the right coordinates on a high-DPI screen.
    padRef.current = pad;

    // signature_pad only dispatches beginStroke/endStroke, so clearing has to be
    // handled by the Clear button: the pad emits nothing when it is cleared.
    pad.addEventListener("endStroke", () => {
      void publish();
    });

    return () => {
      pad.off();
      padRef.current = null;
    };
  }, [publish, onChange, canvasHeight]);

  const handleClear = useCallback(() => {
    padRef.current?.clear();
    setHasInk(false);
    onChange(null);
  }, [onChange]);

  return (
    <div className="space-y-2">
      <p className="text-[11px] font-medium text-gray-500">{t("Sign above")}</p>
      <div className="rounded-xl border border-gray-200 bg-white p-1">
        <canvas
          ref={canvasRef}
          className="block w-full touch-none rounded-lg"
          style={{ cursor: "crosshair", height: `${canvasHeight}px` }}
        />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-gray-400">
          {hasInk ? t("Signed") : t("Not signed yet")}
        </span>
        <button
          type="button"
          disabled={disabled}
          onClick={handleClear}
          className="inline-flex items-center gap-1 rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-bold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217] disabled:opacity-50"
        >
          {t("Clear")}
        </button>
      </div>
    </div>
  );
}